import { type HookCode, RequiredWorkOrderField, type WorkflowJsonObject } from '@atlas/types'
import { Button, Checkbox, Input, ListBoxItem, Select } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import type { TransitionHookDTO } from '@/domain/workflow-admin.types'
import { HOOK_CONFIG, hookCatalogLabel, POST_HOOK_CATALOG, PRE_HOOK_CATALOG } from '@/domain/workflow-hook-catalog'
import { requiredFieldsOf } from '@atlas/core-utils/work-order'
import { REQUIRED_FIELD_LABEL } from '@/domain/required-fields'
import { sortedByLabel } from '@/lib/option-order'

export interface ChecklistOption {
	code: string
	label: string
}

/** `fields_present`'s editor: one checkbox per field it can require. The last one cannot be unticked, an empty list fails validation. */
function RequiredFieldsEditor({
	params,
	onChange,
}: {
	params: WorkflowJsonObject
	onChange: (params: WorkflowJsonObject) => void
}) {
	const picked = requiredFieldsOf(params)
	const toggle = (field: RequiredWorkOrderField) =>
		onChange({
			fields: Object.values(RequiredWorkOrderField).filter((f) => (f === field) !== picked.includes(f)),
		})
	return (
		<div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
			{Object.values(RequiredWorkOrderField).map((field) => {
				const isSelected = picked.includes(field)
				return (
					<Checkbox
						key={field}
						size="sm"
						isSelected={isSelected}
						isDisabled={isSelected && picked.length === 1}
						onChange={() => toggle(field)}
					>
						{REQUIRED_FIELD_LABEL[field]}
					</Checkbox>
				)
			})}
		</div>
	)
}

/** One hook's config editor — a checklist mode/template picker, a field pick, a single free-text field, or nothing. */
function HookConfigEditor({
	hook,
	templates,
	onChange,
}: {
	hook: TransitionHookDTO
	templates: readonly ChecklistOption[]
	onChange: (params: WorkflowJsonObject) => void
}) {
	const config = HOOK_CONFIG[hook.hookCode]
	if (!config) return null
	if (config.kind === 'fields') return <RequiredFieldsEditor params={hook.params} onChange={onChange} />

	if (config.kind === 'text') {
		const value = typeof hook.params[config.paramKey] === 'string' ? (hook.params[config.paramKey] as string) : ''
		return (
			<Input
				aria-label={config.label}
				value={value}
				onChange={(e) => onChange({ [config.paramKey]: e.target.value })}
				placeholder={config.placeholder}
				className="mt-2 font-mono text-xs"
			/>
		)
	}

	const isDynamic = hook.params.mode === 'dynamic'
	return (
		<div className="mt-2 flex gap-2">
			<Select
				aria-label="Checklist mode"
				selectedKey={isDynamic ? 'dynamic' : 'static'}
				onSelectionChange={(key) =>
					// No template to fall back to yet — stay dynamic rather than send an empty checklistTemplateCode (fails validation).
					onChange(
						key === 'dynamic' || !templates[0] ? { mode: 'dynamic' } : { checklistTemplateCode: templates[0].code },
					)
				}
				className="max-w-28"
			>
				<ListBoxItem id="static">Static</ListBoxItem>
				<ListBoxItem id="dynamic">Dynamic</ListBoxItem>
			</Select>
			{!isDynamic ? (
				<Select
					aria-label="Checklist template"
					selectedKey={
						typeof hook.params.checklistTemplateCode === 'string' ? hook.params.checklistTemplateCode : undefined
					}
					onSelectionChange={(key) => onChange({ checklistTemplateCode: key as string })}
				>
					{sortedByLabel(templates).map((t) => (
						<ListBoxItem key={t.code} id={t.code}>
							{t.label}
						</ListBoxItem>
					))}
				</Select>
			) : null}
		</div>
	)
}

function HookRow({
	hook,
	index,
	templates,
	onRemove,
	onUpdateParams,
}: {
	hook: TransitionHookDTO
	index: number
	templates: readonly ChecklistOption[]
	onRemove: () => void
	onUpdateParams: (params: WorkflowJsonObject) => void
}) {
	return (
		<div className="rounded-ds-md border border-ds-border bg-ds-surface p-2.5">
			<div className="flex items-center gap-2">
				<span className="w-3.5 font-mono text-ds-4xs text-ds-text-subtle">{index + 1}</span>
				<span className="flex-1 text-sm">{hookCatalogLabel(hook.hookCode)}</span>
				<button
					type="button"
					onClick={onRemove}
					aria-label={`Remove ${hookCatalogLabel(hook.hookCode)}`}
					className="text-ds-text-subtle"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			</div>
			<HookConfigEditor hook={hook} templates={templates} onChange={onUpdateParams} />
		</div>
	)
}

function HookAttachMenu({
	stage,
	open,
	disabled,
	onToggle,
	onAttach,
}: {
	stage: 'pre' | 'post'
	open: boolean
	disabled: boolean
	onToggle: () => void
	onAttach: (hookCode: HookCode) => void
}) {
	const catalog = stage === 'pre' ? PRE_HOOK_CATALOG : POST_HOOK_CATALOG
	return (
		<div>
			<Button variant="secondary" size="sm" className="w-full" onPress={onToggle} isDisabled={disabled}>
				{`＋ Attach ${stage}-hook`}
			</Button>
			{open ? (
				<div className="mt-2 overflow-hidden rounded-ds-md border border-ds-border">
					{catalog.map((c) => (
						<button
							key={c.code}
							type="button"
							onClick={() => onAttach(c.code)}
							disabled={disabled}
							className="block w-full border-b border-ds-border p-2.5 text-left text-sm last:border-b-0 hover:bg-ds-surface disabled:cursor-not-allowed disabled:opacity-50"
						>
							{c.label}
							<div className="font-mono text-ds-4xs text-ds-text-subtle">{c.description}</div>
						</button>
					))}
				</div>
			) : null}
		</div>
	)
}

/** One stage's hook list (pre or post) — the rows, plus the attach menu at the bottom. */
export function HookSection({
	stage,
	hooks,
	templates,
	emptyHint,
	menuOpen,
	isAttaching,
	onToggleMenu,
	onAttach,
	onRemove,
	onUpdateParams,
}: {
	stage: 'pre' | 'post'
	hooks: readonly TransitionHookDTO[]
	templates: readonly ChecklistOption[]
	emptyHint: string
	menuOpen: boolean
	isAttaching: boolean
	onToggleMenu: () => void
	onAttach: (hookCode: HookCode) => void
	onRemove: (hookId: string) => void
	onUpdateParams: (hookId: string, params: WorkflowJsonObject) => void
}) {
	return (
		<div className="border-t border-ds-border pt-3">
			<div className="mb-2 flex items-center justify-between font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">
				<span className="text-ds-text">{stage === 'pre' ? 'Pre-hooks' : 'Post-hooks'}</span>
				<span>{stage === 'pre' ? 'guards · must pass' : 'fire & forget'}</span>
			</div>
			<div className="mb-2 flex flex-col gap-2">
				{hooks.length === 0 ? (
					<p className="font-mono text-ds-4xs text-ds-text-subtle">{emptyHint}</p>
				) : (
					hooks.map((h, i) => (
						<HookRow
							key={h.hookId}
							hook={h}
							index={i}
							templates={templates}
							onRemove={() => onRemove(h.hookId)}
							onUpdateParams={(params) => onUpdateParams(h.hookId, params)}
						/>
					))
				)}
			</div>
			<HookAttachMenu
				stage={stage}
				open={menuOpen}
				disabled={isAttaching}
				onToggle={onToggleMenu}
				onAttach={onAttach}
			/>
		</div>
	)
}
